import { motion } from 'motion/react';

import type { AnalysisOutput } from '@smartapply/shared';

import { DiagnosisSummary } from './DiagnosisSummary.js';
import { FindingCard } from './FindingCard.js';
import { PillarsBoard } from './PillarsBoard.js';
import { QuickWinsList } from './QuickWinsList.js';
import { RewriteBlock } from './RewriteBlock.js';
import { StrengthsList } from './StrengthsList.js';
import { SaveAnalysisBox } from './SaveAnalysisBox.js';
import { SectionMark } from '../ui/SectionMark.js';
import { Stamp } from '../ui/Stamp.js';
import { MetaDots } from '../ui/MetaDots.js';

interface DiagnosisViewProps {
  output: AnalysisOutput;
  onReset: () => void;
  /** Quando true, esconde o botão "salvar" (tela de detalhe já é uma análise persistida). */
  readOnly?: boolean;
  /** Metadados do PDF para persistência opcional. */
  pdfBytes?: number;
  pdfPages?: number;
}

export function DiagnosisView({
  output,
  onReset,
  readOnly = false,
  pdfBytes,
  pdfPages,
}: DiagnosisViewProps) {
  const critical = output.findings.filter((f) => f.severity === 'critical');
  const suggestions = output.findings.filter((f) => f.severity === 'suggestion');

  const totalQuickWinMinutes = output.quickWins.reduce(
    (s, q) => s + q.estimatedTimeMinutes,
    0,
  );

  return (
    <article>
      <motion.header
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="grid gap-8 border-b border-[var(--border-default)] pb-10 lg:grid-cols-[1fr_320px] lg:gap-16"
      >
        <div>
          <p className="mb-4 inline-block">
            <Stamp>Etapa 03 / 03 · diagnóstico</Stamp>
          </p>
          <h1 className="font-serif text-[30px] font-medium leading-[1.1] tracking-[-0.02em] text-[var(--ink-primary)] sm:text-[40px] lg:text-[44px]">
            Diagnóstico
            <span className="text-[var(--accent-saffron)]">.</span>
          </h1>
          <div className="mt-6 border-l-2 border-[var(--accent-saffron)] py-2 pl-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
              posicionamento detectado
            </p>
            <p className="drop-cap mt-3 font-serif text-xl leading-snug text-[var(--ink-primary)]">
              {output.positioningSentence}
            </p>
          </div>
        </div>
        <aside className="flex flex-col justify-end space-y-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
            metadados da análise
          </p>
          <MetaDots
            rows={[
              { label: 'modelo', value: output.modelUsed, mono: true },
              {
                label: 'gerado',
                value: new Date(output.generatedAt).toLocaleString('pt-BR'),
                mono: true,
              },
              { label: 'duração', value: `${(output.meta.durationMs / 1000).toFixed(1)}s`, mono: true },
              { label: 'prompt', value: output.meta.promptVersion, mono: true },
              {
                label: 'findings',
                value: `${output.findings.length}`,
                mono: true,
              },
              {
                label: 'quick wins',
                value: `${output.quickWins.length} · ~${totalQuickWinMinutes} min`,
                mono: true,
              },
            ]}
          />
        </aside>
      </motion.header>

      {/* Placar por pilar — leitura rápida do que cada finding afeta */}
      <PillarsBoard findings={output.findings} />

      {/* Síntese visual entre o header e as seções detalhadas */}
      <div className="mt-10">
        <DiagnosisSummary output={output} />
      </div>

      <div className="mt-12 space-y-12">
        <section
          className="rounded-sm px-4 py-8 sm:px-6"
          style={{ backgroundColor: 'var(--bg-positive-tint)' }}
        >
          <SectionMark
            index="§1"
            count={output.strengths.length}
            countLabel={output.strengths.length === 1 ? 'força' : 'forças'}
            description="Itens que devem ser preservados em qualquer reescrita."
          >
            Forças
          </SectionMark>
          <StrengthsList strengths={output.strengths} />
        </section>

        {critical.length > 0 && (
          <section
            className="rounded-sm px-4 py-8 sm:px-6"
            style={{ backgroundColor: 'var(--bg-critical-tint)' }}
          >
            <SectionMark
              index="§2"
              count={critical.length}
              countLabel={critical.length === 1 ? 'item' : 'itens'}
              description="Tiram seu perfil de buscas ou pioram conversão. Endereçar antes de qualquer outro ponto."
            >
              Problemas críticos
            </SectionMark>
            <div className="space-y-0">
              {critical.map((f, i) => (
                <FindingCard key={f.id} finding={f} index={i + 1} />
              ))}
            </div>
          </section>
        )}

        {suggestions.length > 0 && (
          <section
            className="rounded-sm px-4 py-8 sm:px-6"
            style={{ backgroundColor: 'var(--bg-suggestion-tint)' }}
          >
            <SectionMark
              index="§3"
              count={suggestions.length}
              countLabel={suggestions.length === 1 ? 'sugestão' : 'sugestões'}
              description="Oportunidades de melhoria. Ordenar pelo seu critério (impacto vs esforço)."
            >
              Sugestões
            </SectionMark>
            <div className="space-y-0">
              {suggestions.map((f, i) => (
                <FindingCard key={f.id} finding={f} index={i + 1} severity="suggestion" />
              ))}
            </div>
          </section>
        )}

        {output.quickWins.length > 0 && (
          <section className="card-paper rounded-sm px-6 py-8">
            <SectionMark
              index="§4"
              count={output.quickWins.length}
              countLabel="fixes pequenos"
              description={`Tempo total estimado: ~${totalQuickWinMinutes} minutos.`}
            >
              Quick wins
            </SectionMark>
            <QuickWinsList quickWins={output.quickWins} findings={output.findings} />
          </section>
        )}

        {output.rewrittenSections.length > 0 && (
          <section className="card-paper rounded-sm px-6 py-8">
            <SectionMark
              index="§5"
              count={output.rewrittenSections.length}
              countLabel="reescritas"
              description="Texto pronto para colar de volta no LinkedIn."
            >
              Reescritas prontas
            </SectionMark>
            <div className="space-y-6">
              {output.rewrittenSections.map((r, i) => (
                <div key={i}>
                  <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
                    R.{(i + 1).toString().padStart(2, '0')} · {r.section} ·{' '}
                    <span className="tabular-nums">{r.charCount}</span> chars
                    {r.context ? ` · ${r.context}` : ''}
                  </p>
                  <RewriteBlock title={r.section} text={r.text} rationale={r.rationale} />
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {!readOnly && (
        <div className="mt-12">
          <SaveAnalysisBox
            output={output}
            pdfBytes={pdfBytes}
            pdfPages={pdfPages}
          />
        </div>
      )}

      {!readOnly && (
        <footer className="mt-16 flex flex-col gap-3 border-t border-[var(--border-default)] pt-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="font-sans text-xs italic text-[var(--ink-tertiary)]">
            Esta análise fica disponível enquanto a aba estiver aberta. Pra rodar com outro PDF,
            use o botão ao lado.
          </p>
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-2 self-start rounded-sm border border-[var(--ink-primary)] bg-[var(--ink-primary)] px-4 py-2 font-sans text-sm text-[var(--bg-paper)] transition-opacity hover:opacity-90 sm:self-auto"
          >
            <span aria-hidden className="font-mono">↑</span>
            Subir novo PDF
          </button>
        </footer>
      )}

      {readOnly && (
        <footer className="mt-16 border-t border-[var(--border-default)] pt-6">
          <button
            type="button"
            onClick={onReset}
            className="font-sans text-sm text-[var(--ink-secondary)] underline-offset-4 hover:underline"
          >
            ← voltar
          </button>
        </footer>
      )}
    </article>
  );
}
